// A IMPRENSA: o que se diz do seu time por fora.
//
// ============================================================================
// POR QUE ISTO EXISTE
//
// O 82 se vende como o jogo do basquete brasileiro, e ate aqui nada dentro
// dele SOAVA brasileiro. O `state.news` ja existia, mas so como recibo do que
// aconteceu: "Vitoria 88 a 81", "Fulano deixou o clube". Ninguem tira print de
// um recibo.
//
// Aqui a mesma informacao ganha voz de imprensa esportiva: a sequencia vira
// "quarta seguida, e o ginasio vaiou"; a vitoria sobre o lider vira zebra; a
// meta da diretoria vira cobranca publica. Nao muda uma regra do jogo -- muda
// o que a pessoa lembra dele depois.
//
// ============================================================================
// AS TRES REGRAS DESTE ARQUIVO
//
// 1. NO MAXIMO UMA MANCHETE POR RODADA. A tentacao e disparar tudo que
//    casa; com 82 rodadas isso vira ruido e a pessoa para de ler. Os gatilhos
//    sao ordenados por prioridade e so o primeiro publica.
//
// 2. TUDO SAI DA SEMENTE. `state.news` e GRAVADO no save. Texto sorteado com
//    Math.random faria a mesma carreira contar historias diferentes a cada
//    recarga -- e o save na nuvem passaria a divergir entre aparelhos.
//
// 3. TEXTO CURTO E SEM ACENTO. Curto porque o save tem orcamento (ver
//    PLANO.md) e a lista guarda 40 itens; sem acento porque e a convencao de
//    todo texto que nasce no motor -- `Vitoria`, `Voce assumiu o ...`.
// ============================================================================

import { makeRng } from './rng.js';
import { sortedStandings } from './season.js';
import { seasonTarget } from './jobs.js';

/** Escolhe uma das variacoes, pela semente. */
const uma = (rng, lista) => lista[rng.int(lista.length)];

/** A sequencia atual do clube: {tipo: 'V'|'D', n} olhando os resultados. */
function sequencia(state) {
  let tipo = null;
  let n = 0;
  // `results` cresce no fim, entao a rodada mais recente esta no final.
  for (let i = state.results.length - 1; i >= 0; i--) {
    const r = state.results[i];
    if (r.season !== state.season) break;
    const meuMando = r.home === state.clubId;
    const meuFora = r.away === state.clubId;
    if (!meuMando && !meuFora) continue;
    const meus = meuMando ? r.hs : r.as;
    const deles = meuMando ? r.as : r.hs;
    const atual = meus > deles ? 'V' : 'D';
    if (tipo === null) tipo = atual;
    if (atual !== tipo) break;
    n += 1;
  }
  return { tipo, n };
}

/** O ultimo jogo do clube nesta temporada, ja do ponto de vista dele. */
function ultimoJogo(state) {
  for (let i = state.results.length - 1; i >= 0; i--) {
    const r = state.results[i];
    if (r.season !== state.season) break;
    const meuMando = r.home === state.clubId;
    const meuFora = r.away === state.clubId;
    if (!meuMando && !meuFora) continue;
    return {
      adversario: meuMando ? r.away : r.home,
      meus: meuMando ? r.hs : r.as,
      deles: meuMando ? r.as : r.hs,
      emCasa: meuMando,
    };
  }
  return null;
}

const nomeDe = (state, id) => state.teams.find((t) => t.id === id)?.name || id;

/**
 * A manchete da rodada, se houver uma.
 *
 * ⚠️ A ordem do array E a prioridade. Zebra vale mais que sequencia, que vale
 * mais que marco de calendario -- senao a rodada em que voce derrubou o lider
 * seria noticiada como "metade da temporada".
 */
export function manchete(state) {
  if (state.unemployed) return null;

  const rng = makeRng(`imprensa|${state.seed}|${state.season}|${state.round}`);
  const eu = state.standings[state.clubId];
  if (!eu) return null;

  const meuTime = state.teams.find((t) => t.id === state.clubId);
  const clube = meuTime?.name || 'seu clube';
  const tabela = sortedStandings(state.standings);
  const posicao = tabela.findIndex((r) => r.teamId === state.clubId) + 1;
  const total = tabela.length;
  const jogo = ultimoJogo(state);
  const seq = sequencia(state);
  const jogos = (eu.wins || 0) + (eu.losses || 0);

  // ------------------------------------------------------------ a zebra ---
  /* Ganhar de quem esta em primeiro (ou perder para o ultimo) e a unica coisa
     que a imprensa noticia mesmo em rodada morta de meio de temporada. */
  if (jogo && jogos >= 4) {
    const advPos = tabela.findIndex((r) => r.teamId === jogo.adversario) + 1;
    const adv = nomeDe(state, jogo.adversario);
    const ganhei = jogo.meus > jogo.deles;
    if (ganhei && advPos > 0 && advPos <= 2 && posicao > 5) {
      return {
        kind: 'imprensa',
        assunto: 'zebra',
        text: uma(rng, [
          `Zebra: o ${clube} derrubou o lider ${adv} por ${jogo.meus} a ${jogo.deles}.`,
          `Ninguem esperava. ${clube} ${jogo.meus}, ${adv} ${jogo.deles} -- o lider caiu.`,
          `O ${clube} bateu o ${adv}, primeiro colocado, e a tabela ficou estranha.`,
        ]),
      };
    }
    if (!ganhei && advPos >= total - 1 && posicao <= 6) {
      return {
        kind: 'imprensa',
        assunto: 'vexame',
        text: uma(rng, [
          `Vexame: derrota para o lanterna ${adv}, ${jogo.deles} a ${jogo.meus}.`,
          `O ${clube} perdeu para o ultimo colocado. A cobranca comeca amanha.`,
          `${adv}, ultimo da tabela, venceu o ${clube} por ${jogo.deles} a ${jogo.meus}.`,
        ]),
      };
    }
  }

  // -------------------------------------------------------- a sequencia ---
  /* Tres e o menor numero que a imprensa chama de sequencia. Abaixo disso e
     so "o time perdeu ontem", e ninguem escreve materia sobre isso. */
  /* ⚠️ TODA variacao usa `${seq.n}`. A primeira versao tinha "Terceira seguida
     do X" no mesmo sorteio das outras, e ela saia com a sequencia em 4 ou 5 --
     o jogo contradizendo a propria tabela na frente do jogador. Numero em
     texto de manchete so pode vir da variavel. */
  if (seq.n >= 3) {
    if (seq.tipo === 'V') {
      return {
        kind: 'imprensa',
        assunto: 'embalo',
        text: seq.n >= 6
          ? uma(rng, [
            `${seq.n} vitorias seguidas. O ${clube} virou assunto no basquete nacional.`,
            `Ninguem para o ${clube}: sao ${seq.n} seguidas, e o ginasio lota.`,
          ])
          : uma(rng, [
            `${seq.n} seguidas do ${clube}. A torcida voltou a acreditar.`,
            `${seq.n} vitorias em sequencia -- o ${clube} embalou.`,
            `O ${clube} venceu de novo. Sao ${seq.n} seguidas.`,
          ]),
      };
    }
    return {
      kind: 'imprensa',
      assunto: 'crise',
      text: seq.n >= 6
        ? uma(rng, [
          `${seq.n} derrotas seguidas. A diretoria evitou falar com a imprensa.`,
          `A crise virou rotina: ${seq.n} jogos sem vencer, e o ginasio vaiou.`,
        ])
        : uma(rng, [
          `${seq.n} derrotas seguidas e o ginasio vaiou no fim do jogo.`,
          `O ${clube} nao vence ha ${seq.n} jogos. A pressao aumentou.`,
          `${seq.n} derrotas seguidas. Perguntaram do seu cargo na entrevista.`,
        ]),
    };
  }

  // ------------------------------------------------- a cobranca da meta ---
  /* So no ultimo terco: cobrar meta na rodada 5 e barulho, porque nao ha
     temporada suficiente para julgar ninguem. */
  const rodadasTotais = state.schedule.length || 1;
  const fim = state.round / rodadasTotais;
  if (fim >= 0.7 && jogos >= 6) {
    const meta = seasonTarget(meuTime, state.teams);
    const aprov = eu.wins / Math.max(1, jogos);
    if (aprov < meta - 0.12) {
      return {
        kind: 'imprensa',
        assunto: 'cobranca',
        text: uma(rng, [
          `A diretoria queria ${Math.round(meta * 100)}% de vitorias. Voce esta em ${Math.round(aprov * 100)}%.`,
          `Reta final com o ${clube} em ${posicao}o. A meta da temporada ja parece longe.`,
          `Nos bastidores, a diretoria do ${clube} avalia a temporada como perdida.`,
        ]),
      };
    }
    if (aprov > meta + 0.12) {
      return {
        kind: 'imprensa',
        assunto: 'elogio',
        text: uma(rng, [
          `O ${clube} passou da meta da temporada com rodadas de sobra.`,
          `${posicao}o lugar e temporada acima do esperado. Seu nome subiu no mercado.`,
        ]),
      };
    }
  }

  // ------------------------------------------------------- os marcos ------
  if (state.round === Math.floor(rodadasTotais / 2)) {
    return {
      kind: 'imprensa',
      assunto: 'marco',
      text: `Metade da temporada: ${clube} em ${posicao}o, com ${eu.wins}V e ${eu.losses}D.`,
    };
  }
  if (rodadasTotais - state.round === 5) {
    return {
      kind: 'imprensa',
      assunto: 'marco',
      text: `Faltam 5 rodadas e o ${clube} esta em ${posicao}o de ${total}.`,
    };
  }

  return null;
}

/**
 * Publica a manchete no `state.news`, se houver.
 *
 * ⚠️ Muta o estado, como o resto do career.js -- e chamada de dentro de
 * `advanceRound`, que ja e a unica porta de escrita.
 */
/**
 * Quantas rodadas o mesmo assunto tem que esperar para voltar.
 *
 * ⚠️ SEM ISTO A COLUNA VIRA DISCO RISCADO. Medido numa temporada de NBB antes
 * do intervalo existir: "O Mogi passou da meta da temporada" saiu QUATRO vezes
 * em sete rodadas, porque a condicao continua verdadeira depois de virar
 * noticia. Manchete e sobre a MUDANCA, nao sobre o estado.
 *
 * Os numeros seguem a logica do assunto: elogio e cobranca descrevem uma
 * situacao que dura semanas, entao esperam muito; zebra e um jogo unico, e
 * pode repetir mais cedo.
 */
const INTERVALO = {
  zebra: 4, vexame: 4, embalo: 5, crise: 5, cobranca: 10, elogio: 10, marco: 1,
};

export function publicarManchete(state) {
  const nota = manchete(state);
  if (!nota) return null;

  const espera = INTERVALO[nota.assunto] ?? 5;
  const recente = state.news.some(
    (n) => n.kind === 'imprensa'
      && n.season === state.season
      && (n.assunto === nota.assunto || n.text === nota.text)
      && state.round - (n.round ?? 0) < espera,
  );
  if (recente) return null;

  state.news.unshift({ round: state.round, season: state.season, ...nota });
  state.news.length = Math.min(state.news.length, 40);
  return nota;
}
